import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Container, Card, Table, Button, Form, InputGroup, Badge, Alert, Spinner, Modal } from 'react-bootstrap'
import { fetchTargets, createTarget, runComplianceCheck, ComplianceCheckResult } from '../services/api'

export default function Targets() {
  const queryClient = useQueryClient()
  const [newUrl, setNewUrl] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [showResultModal, setShowResultModal] = useState(false)
  const [lastResult, setLastResult] = useState<ComplianceCheckResult | null>(null)
  const [scanningTargetId, setScanningTargetId] = useState<string | null>(null)

  const { data: targets, isLoading, error } = useQuery({
    queryKey: ['targets'],
    queryFn: fetchTargets,
  })

  const addTargetMutation = useMutation({
    mutationFn: createTarget,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['targets'] })
      setNewUrl('')
      setNewDescription('')
    },
  })

  const scanMutation = useMutation({
    mutationFn: runComplianceCheck,
    onSuccess: (result) => {
      setLastResult(result)
      setShowResultModal(true)
      setScanningTargetId(null)
      queryClient.invalidateQueries({ queryKey: ['targets'] })
      queryClient.invalidateQueries({ queryKey: ['metrics'] })
      queryClient.invalidateQueries({ queryKey: ['findings'] })
      queryClient.invalidateQueries({ queryKey: ['actions'] })
    },
    onError: () => {
      setScanningTargetId(null)
    },
  })

  const handleAddTarget = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newUrl.trim()) return
    addTargetMutation.mutate({ url: newUrl, description: newDescription })
  }

  const handleScan = (url: string, targetId: string) => {
    setScanningTargetId(targetId)
    scanMutation.mutate(url)
  }

  const getHealthBadge = (status?: string) => {
    switch (status) {
      case 'Reachable':
        return <Badge bg="success">Reachable</Badge>
      case 'Unreachable':
        return <Badge bg="danger">Unreachable</Badge>
      case 'Broken':
        return <Badge bg="warning">Broken</Badge>
      default:
        return <Badge bg="secondary">Unknown</Badge>
    }
  }

  return (
    <Container className="py-4">
      <h2 className="mb-4">
        <i className="bi bi-globe me-2"></i>
        Target Websites
      </h2>

      <Alert variant="info" className="mb-4">
        <strong>Targets</strong> are specific product page URLs on distributor websites. Each distributor can have many targets (one per product page). Add distributors first, then discover or add targets per product.
      </Alert>

      {/* Add New Target Form */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-primary text-white">
          <i className="bi bi-plus-circle me-2"></i>
          Add New Website to Monitor
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleAddTarget}>
            <Form.Group className="mb-3">
              <Form.Label>Website URL</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <i className="bi bi-link-45deg"></i>
                </InputGroup.Text>
                <Form.Control
                  type="url"
                  placeholder="https://example.com/product-page"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  required
                />
              </InputGroup>
              <Form.Text className="text-muted">
                Enter the full URL of the webpage to monitor for compliance
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Product page for XYZ insulation"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
              />
            </Form.Group>
            <div className="d-flex gap-2">
              <Button 
                type="submit" 
                variant="primary"
                disabled={addTargetMutation.isPending || !newUrl.trim()}
              >
                {addTargetMutation.isPending ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Adding...
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-lg me-2"></i>
                    Add Target
                  </>
                )}
              </Button>
              <Button 
                type="button" 
                variant="success"
                disabled={scanMutation.isPending || !newUrl.trim()}
                onClick={() => {
                  if (newUrl.trim()) {
                    setScanningTargetId('new')
                    scanMutation.mutate(newUrl)
                  }
                }}
              >
                {scanMutation.isPending && scanningTargetId === 'new' ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <i className="bi bi-search me-2"></i>
                    Add & Scan Now
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {addTargetMutation.isError && (
        <Alert variant="danger" className="mb-4">
          Failed to add target: {(addTargetMutation.error as Error).message}
        </Alert>
      )}

      {scanMutation.isError && (
        <Alert variant="danger" className="mb-4">
          Scan failed: {(scanMutation.error as Error).message}
        </Alert>
      )}

      {/* Targets List */}
      <Card className="shadow-sm">
        <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
          <span>
            <i className="bi bi-list-ul me-2"></i>
            Monitored Websites
          </span>
          <Badge bg="light" text="dark">{targets?.length || 0} targets</Badge>
        </Card.Header>
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Loading targets...</p>
            </div>
          ) : error ? (
            <Alert variant="danger" className="m-3">
              Failed to load targets: {(error as Error).message}
            </Alert>
          ) : targets && targets.length > 0 ? (
            <Table hover responsive className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>URL</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Health</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {targets.map((target) => (
                  <tr key={target.id}>
                    <td>
                      <a href={target.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                        {target.url.length > 60 ? target.url.substring(0, 60) + '...' : target.url}
                        <i className="bi bi-box-arrow-up-right ms-1 small"></i>
                      </a>
                    </td>
                    <td className="text-muted">{target.description || '-'}</td>
                    <td>
                      <Badge bg="info">{target.contentType || 'Page'}</Badge>
                    </td>
                    <td>{getHealthBadge(target.healthStatus)}</td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="outline-success"
                        disabled={scanMutation.isPending && scanningTargetId === target.id}
                        onClick={() => handleScan(target.url, target.id)}
                      >
                        {scanMutation.isPending && scanningTargetId === target.id ? (
                          <>
                            <Spinner size="sm" className="me-1" />
                            Scanning...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-play-fill me-1"></i>
                            Scan
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox display-4"></i>
              <p className="mt-3">No targets added yet. Add a website URL above to get started.</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Scan Result Modal */}
      <Modal show={showResultModal} onHide={() => setShowResultModal(false)} size="lg">
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>
            <i className="bi bi-check-circle me-2"></i>
            Compliance Scan Complete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {lastResult && (
            <>
              <div className="mb-3">
                <strong>URL:</strong> {lastResult.url}
              </div>
              <div className="row mb-3">
                <div className="col-md-4">
                  <Card className="text-center bg-light">
                    <Card.Body>
                      <h3 className="mb-0">{lastResult.findings?.length || 0}</h3>
                      <small className="text-muted">Findings</small>
                    </Card.Body>
                  </Card>
                </div>
                <div className="col-md-4">
                  <Card className="text-center bg-light">
                    <Card.Body>
                      <h3 className="mb-0">{lastResult.actions?.length || 0}</h3>
                      <small className="text-muted">Actions Created</small>
                    </Card.Body>
                  </Card>
                </div>
                <div className="col-md-4">
                  <Card className="text-center bg-light">
                    <Card.Body>
                      <h3 className="mb-0">
                        <Badge bg={lastResult.status === 'Completed' ? 'success' : 'warning'}>
                          {lastResult.status}
                        </Badge>
                      </h3>
                      <small className="text-muted">Status</small>
                    </Card.Body>
                  </Card>
                </div>
              </div>
              {lastResult.observedFacts && (
                <div className="mt-3">
                  <strong>Extracted Facts:</strong>
                  <ul className="mt-2">
                    <li>Page Title: {lastResult.observedFacts.facts?.page_title || 'N/A'}</li>
                    <li>Links Found: {lastResult.observedFacts.facts?.links_found || 0}</li>
                    <li>Links Valid: {lastResult.observedFacts.facts?.links_valid || 0}</li>
                    <li>Text Length: {lastResult.observedFacts.facts?.text_length || 0} characters</li>
                  </ul>
                </div>
              )}
              {lastResult.findings && lastResult.findings.length > 0 && (
                <div className="mt-3">
                  <strong>Findings:</strong>
                  <Table size="sm" className="mt-2">
                    <thead>
                      <tr>
                        <th>Severity</th>
                        <th>Description</th>
                        <th>Clause</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lastResult.findings.map((f, i) => (
                        <tr key={i}>
                          <td>
                            <Badge bg={f.severity === 'Critical' ? 'danger' : f.severity === 'High' ? 'warning' : 'info'}>
                              {f.severity}
                            </Badge>
                          </td>
                          <td>{f.description}</td>
                          <td>{f.clause}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResultModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={() => {
            setShowResultModal(false)
            window.location.href = '/findings'
          }}>
            View All Findings
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

